import camelCaseToKebabCase from './camel-case-to-kebab-case';

interface GetBodyClassNameParams {
  pathname: string;
  collection: {
    type: string;
    templateName: string;
    design?: {
      disableDecorations?: boolean;
    };
  };
}

interface DesignVariables {
  [key: string]: string | number | boolean | undefined;
}

const BodyClassNames = {
  BASE: 'ScrAppBook',
  SHOP_NEW: 'ShopNew',
  SIMPLE_FILIGREE: 'SimpleFiligree',
  DISABLE_DECORATIONS: 'disable-decorations',
  DEFAULT_TEMPLATE: 'dark',
  NEW_DESIGN: 'new-design'
} as const;

const CollectionType = {
  SCRAPPBOOK2: 'ScrAppBook2',
  SCRAPPBOOK: 'ScrAppBook'
} as const;

const Routes = {
  SHOP_NEW: '/shop-new',
  PHOTOBOOK: '/photobook'
} as const;

export function getBodyClassName({ pathname, collection }: GetBodyClassNameParams): string {
  const { type, templateName, design } = collection;

  const isShopNewRoute = pathname.includes(Routes.SHOP_NEW);
  const isPhotobookRoute = pathname.includes(Routes.PHOTOBOOK);

  const isModernTemplate = type === CollectionType.SCRAPPBOOK2;
  const hasDisableDecorations = design?.disableDecorations === true;

  let className = '';

  if (isShopNewRoute || isPhotobookRoute) {
    className = `${BodyClassNames.BASE} ${BodyClassNames.SHOP_NEW}`;
  } else if (isModernTemplate) {
    const template = templateName || BodyClassNames.DEFAULT_TEMPLATE;
    className = `${BodyClassNames.BASE} ${template}`;
  } else {
    className = `${BodyClassNames.BASE} ${BodyClassNames.SIMPLE_FILIGREE}`;
  }

  // Add disable decorations class if needed
  if (hasDisableDecorations && !className.includes(BodyClassNames.DISABLE_DECORATIONS)) {
    className += ` ${BodyClassNames.DISABLE_DECORATIONS}`;
  }

  return className;
}

// Toggles `.new-design` on <html>, which scopes the SBK-9080 font redesign (Noto Sans Display).
export function toggleNewDesignClass(enabled: boolean): void {
  document.documentElement.classList.toggle(BodyClassNames.NEW_DESIGN, enabled);
}

/**
 * Applies design variables as CSS custom properties to the body element.
 * This function sets CSS variables like --primary-color, --secondary-color, etc.
 * on the element with class 'ScrAppBook' (typically the body element).
 *
 * @param designVariables - Object containing design variables from collection.design.variables
 */
// The only design variables the redesign consumes as CSS custom properties. `font`/`headingFont`
// become --title-font/--title-heading-font (see the map below); --font/--heading-font and the
// per-template tokens (--decorations-color, --hover-color-spring-vibes) are owned by
// modules/ScrAppBookNew/styles itself, so the API must not supply them. The UI pair is written
// as --ui-background/--ui-text by DesignThemeProvider, after the palette's fallback chain.
const NEW_DESIGN_CSS_VARIABLES = new Set([
  'primaryColor',
  'secondaryColor',
  'accentColor',
  'coverTextColor',
  'primaryColorRgb',
  'secondaryColorRgb',
  'font',
  'headingFont'
]);

export function applyDesignVariablesToBody(
  designVariables: DesignVariables | undefined,
  isNewDesign = false
): void {
  if (!designVariables) return;

  const [templateWrapper] = document.getElementsByClassName(BodyClassNames.BASE);

  if (!templateWrapper) return;

  // The brand custom font is intentionally NOT written to --font / --heading-font
  // (those are the app-wide default — Noto Sans Display). It is routed to the
  // title-only variables, so the gallery hero title keeps the brand font while the
  // rest of the UI stays on the default. See the "Font system" block in
  // scss/vendors/site/scrappbook.scss.
  const newDesignFontVariableMap: { [key: string]: string } = {
    font: '--title-font',
    headingFont: '--title-heading-font'
  };

  Object.entries(designVariables).forEach(([key, value]) => {
    if (!value) return;
    // The redesign themes off six tokens (plus their channel form and the brand fonts), so only
    // those reach the DOM. The rest of design.variables still feeds theme.colors, which shared
    // src/components/** read — this filter is the CSS channel only.
    if (isNewDesign && !NEW_DESIGN_CSS_VARIABLES.has(key)) return;

    const isFontVariable = key === 'font' || key === 'headingFont';
    const cssPropertyName =
      isNewDesign && isFontVariable
        ? newDesignFontVariableMap[key]
        : `--${camelCaseToKebabCase(key)}`;
    const cssPropertyValue = isFontVariable ? `'${value}'` : String(value);

    (templateWrapper as HTMLElement).style.setProperty(cssPropertyName, cssPropertyValue);
  });
}
